import React, { useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import StockScanPopup from "./StockScanPopup";
import { LabelPage, LabelPages } from "./QRLabelSheet";
import { buildScanUrl, parseScanParams } from "./stockQrUtils";
import {
  DEFAULT_SETTINGS,
  LABEL_TEMPLATES,
  MM_TO_PX,
  PAPERS,
  buildPages,
  computeLayout,
  loadSettings,
  saveSettings,
} from "./qrLabelUtils";
import "./StockQR.scss";

const PREVIEW_W = 300; // px

// Number input that can be cleared while typing.
// emptyIsZero: for "0 = auto" fields, 0 is shown as an empty box (placeholder explains) and empty means 0.
const NumberField = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  hint,
  emptyIsZero = false,
  placeholder,
}) => {
  const [text, setText] = useState(null); // null = not being edited

  const shown =
    text !== null
      ? text
      : emptyIsZero && Number(value) === 0
        ? ""
        : String(value);

  const handleChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    if (raw === "") {
      if (emptyIsZero) onChange(0);
      return; // otherwise wait: the value is restored on blur
    }
    const n = Number(raw);
    if (!Number.isNaN(n)) onChange(n);
  };

  const handleBlur = () => {
    if (text !== null && text !== "") {
      const n = Number(text);
      if (!Number.isNaN(n)) {
        let c = n;
        if (min !== undefined) c = Math.max(min, c);
        if (max !== undefined) c = Math.min(max, c);
        if (c !== n) onChange(c);
      }
    }
    setText(null);
  };

  return (
    <label className="qrd-field">
      <span>{label}</span>
      <div className="qrd-input-wrap">
        <input
          type="number"
          value={shown}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {suffix && <em>{suffix}</em>}
      </div>
      {hint && <small>{hint}</small>}
    </label>
  );
};

const Toggle = ({ label, checked, onChange }) => (
  <label className="qrd-toggle">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span>{label}</span>
  </label>
);

/**
 * target: { scope: "group" | "variant", type, materialId, materialName, materialCode,
 *           combinations: [{ id, name, sku, racks }] }
 */
const StockQRModal = ({ target, onClose }) => {
  const isGroup = target.scope === "group";
  const [settings, setSettings] = useState(loadSettings);
  const [selected, setSelected] = useState(() => {
    const keys = new Set(target.combinations.map((c) => `v${c.id}`));
    return keys; // product-level QR is off by default
  });
  const [previewPage, setPreviewPage] = useState(0);
  const [testParams, setTestParams] = useState(null);

  // Remember every change in this browser
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const numOr = (v, fallback) =>
    v === "" || Number.isNaN(Number(v)) ? fallback : Number(v);

  /* ----- what can be printed ----- */
  const allItems = useMemo(() => {
    const base = { type: target.type, materialId: target.materialId };
    const variants = target.combinations.map((c) => ({
      key: `v${c.id}`,
      kind: "variant",
      url: buildScanUrl({ ...base, combinationIds: [c.id] }),
      product: target.materialName,
      variant: c.name,
      sku: c.sku || "",
      racks: c.racks || "",
    }));
    const productItem = isGroup
      ? {
          key: "p",
          kind: "product",
          url: buildScanUrl({
            ...base,
            combinationIds: target.combinations.map((c) => c.id),
          }),
          product: target.materialName,
          variant: "All variants",
          sku: target.materialCode || "",
          racks: "",
        }
      : null;
    return { variants, productItem };
  }, [target, isGroup]);

  const items = useMemo(() => {
    const list = allItems.variants.filter((v) => selected.has(v.key));
    if (allItems.productItem && selected.has("p"))
      list.unshift(allItems.productItem);
    return list;
  }, [allItems, selected]);

  const toggleKey = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /* ----- layout ----- */
  const layout = useMemo(
    () => computeLayout(items.length ? items : allItems.variants, settings),
    [items, allItems, settings],
  );
  const { pages, labelCount } = useMemo(
    () => buildPages(items, settings, layout),
    [items, settings, layout],
  );

  useEffect(() => {
    if (previewPage > Math.max(0, pages.length - 1))
      setPreviewPage(Math.max(0, pages.length - 1));
  }, [pages.length, previewPage]);

  const scale = PREVIEW_W / (layout.pw * MM_TO_PX);
  const previewH = layout.ph * MM_TO_PX * scale;

  /* ----- actions ----- */
  const print = () => {
    if (!pages.length) return;
    const html = renderToStaticMarkup(
      <LabelPages pages={pages} layout={layout} s={settings} />,
    );
    const iframe = document.createElement("iframe");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>QR labels</title>
       <style>
         @page { size: ${layout.pw}mm ${layout.ph}mm; margin: 0; }
         html, body { margin: 0; padding: 0; background: #fff; }
         * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
       </style></head><body>${html}</body></html>`,
    );
    doc.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 60000);
    }, 400);
  };

  const openTestScan = () => {
    const first = items[0] || allItems.variants[0];
    if (first) setTestParams(parseScanParams(new URL(first.url).search));
  };

  if (testParams) {
    return (
      <StockScanPopup params={testParams} onClose={() => setTestParams(null)} />
    );
  }

  const title = isGroup
    ? target.materialName
    : `${target.materialName} — ${target.combinations[0]?.name || "Default"}`;
  const perPageInfo = `${layout.cols} × ${layout.rows} = ${layout.perPage} per page`;

  return (
    <div
      className="scan-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="scan-popup qr-modal" onClick={(e) => e.stopPropagation()}>
        <header className="scan-header">
          <div>
            <span className="scan-kind">Print QR labels</span>
            <h3>{title}</h3>
          </div>
          <button
            type="button"
            className="scan-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="scan-body qrd-layout">
          {/* ---------------- settings ---------------- */}
          <div className="qrd-settings">
            <section>
              <h4>1. What to print</h4>
              {isGroup ? (
                <>
                  <div className="qrd-row-actions">
                    <button
                      type="button"
                      onClick={() =>
                        setSelected(
                          new Set(
                            allItems.variants
                              .map((v) => v.key)
                              .concat(selected.has("p") ? ["p"] : []),
                          ),
                        )
                      }
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSelected(new Set(selected.has("p") ? ["p"] : []))
                      }
                    >
                      Clear
                    </button>
                  </div>
                  <div className="qrd-items">
                    {allItems.variants.map((v) => (
                      <Toggle
                        key={v.key}
                        label={v.variant || "Default"}
                        checked={selected.has(v.key)}
                        onChange={() => toggleKey(v.key)}
                      />
                    ))}
                    <Toggle
                      label={`Also one product QR (opens all ${target.combinations.length} variants)`}
                      checked={selected.has("p")}
                      onChange={() => toggleKey("p")}
                    />
                  </div>
                </>
              ) : (
                <p className="qrd-note">
                  One QR for this variant. Use “Copies” to print several for
                  your boxes.
                </p>
              )}
              <NumberField
                label="Copies of each QR"
                value={settings.copies}
                min={1}
                max={500}
                onChange={(v) => update({ copies: numOr(v, 1) })}
              />
            </section>

            <section>
              <h4>2. Label design</h4>
              <div className="qrd-templates">
                {LABEL_TEMPLATES.map((t) => (
                  <label
                    key={t.id}
                    className={`qrd-template ${settings.template === t.id ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="label-template"
                      checked={settings.template === t.id}
                      onChange={() => update({ template: t.id })}
                    />
                    <span>
                      <strong>{t.label}</strong>
                      <small>{t.description}</small>
                    </span>
                  </label>
                ))}
              </div>

              <div className="qrd-size">
                <div className="qrd-size-head">
                  <span>QR size</span>
                  <strong>{settings.qrSizeCm} cm</strong>
                </div>
                <input
                  type="range"
                  min="2"
                  max="10"
                  step="0.5"
                  value={settings.qrSizeCm}
                  onChange={(e) => update({ qrSizeCm: Number(e.target.value) })}
                />
                <div className="qrd-chips">
                  {[3, 4, 4.5, 5, 6].map((cm) => (
                    <button
                      key={cm}
                      type="button"
                      className={settings.qrSizeCm === cm ? "active" : ""}
                      onClick={() => update({ qrSizeCm: cm })}
                    >
                      {cm} cm
                    </button>
                  ))}
                </div>
              </div>

              {settings.template !== "qr_only" &&
                settings.template !== "compact" && (
                  <div className="qrd-toggles">
                    <Toggle
                      label="Product name"
                      checked={settings.showProduct}
                      onChange={(v) => update({ showProduct: v })}
                    />
                    <Toggle
                      label="Variant name"
                      checked={settings.showVariant}
                      onChange={(v) => update({ showVariant: v })}
                    />
                    <Toggle
                      label="SKU"
                      checked={settings.showSku}
                      onChange={(v) => update({ showSku: v })}
                    />
                    <Toggle
                      label="Rack numbers"
                      checked={settings.showRack}
                      onChange={(v) => update({ showRack: v })}
                    />
                  </div>
                )}

              <div className="qrd-grid2">
                <NumberField
                  label="Text size"
                  value={settings.fontPt}
                  min={5}
                  max={16}
                  step={0.5}
                  suffix="pt"
                  onChange={(v) => update({ fontPt: numOr(v, 8) })}
                />
                <NumberField
                  label="Label padding"
                  value={settings.paddingMm}
                  min={0}
                  max={10}
                  step={0.5}
                  suffix="mm"
                  onChange={(v) => update({ paddingMm: numOr(v, 2.5) })}
                />
              </div>
              <label className="qrd-field">
                <span>Scan reliability</span>
                <select
                  value={settings.errorLevel}
                  onChange={(e) => update({ errorLevel: e.target.value })}
                >
                  <option value="L">Low (simplest code)</option>
                  <option value="M">Medium (recommended)</option>
                  <option value="Q">High (scuffed / curved boxes)</option>
                  <option value="H">Highest (densest code)</option>
                </select>
              </label>
              <Toggle
                label="Dashed cut guide around each label"
                checked={settings.showBorder}
                onChange={(v) => update({ showBorder: v })}
              />
            </section>

            <section>
              <h4>3. Page</h4>
              <div className="qrd-grid2">
                <label className="qrd-field">
                  <span>Paper</span>
                  <select
                    value={settings.paper}
                    onChange={(e) => update({ paper: e.target.value })}
                  >
                    {Object.entries(PAPERS).map(([id, p]) => (
                      <option key={id} value={id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="qrd-field">
                  <span>Orientation</span>
                  <select
                    value={settings.orientation}
                    onChange={(e) => update({ orientation: e.target.value })}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </label>
                <NumberField
                  label="Page margin"
                  value={settings.marginMm}
                  min={0}
                  max={30}
                  step={0.5}
                  suffix="mm"
                  onChange={(v) => update({ marginMm: numOr(v, 8) })}
                />
                <NumberField
                  label="Gap between labels"
                  value={settings.gapMm}
                  min={0}
                  max={20}
                  step={0.5}
                  suffix="mm"
                  onChange={(v) => update({ gapMm: numOr(v, 2) })}
                />
                <NumberField
                  label="Columns"
                  value={settings.columns}
                  min={0}
                  max={30}
                  emptyIsZero
                  placeholder="Auto"
                  hint={`Empty = auto (${layout.autoCols} fit)`}
                  onChange={(v) => update({ columns: numOr(v, 0) })}
                />
                <NumberField
                  label="Rows"
                  value={settings.rows}
                  min={0}
                  max={50}
                  emptyIsZero
                  placeholder="Auto"
                  hint={`Empty = auto (${layout.autoRows} fit)`}
                  onChange={(v) => update({ rows: numOr(v, 0) })}
                />
                <NumberField
                  label="Number of pages"
                  value={settings.pages}
                  min={0}
                  max={200}
                  emptyIsZero
                  placeholder="Auto"
                  hint="Empty = as many as needed"
                  onChange={(v) => update({ pages: numOr(v, 0) })}
                />
                <NumberField
                  label="Skip first labels"
                  value={settings.skip}
                  min={0}
                  max={500}
                  emptyIsZero
                  placeholder="None"
                  hint="For a partly used sticker sheet"
                  onChange={(v) => update({ skip: numOr(v, 0) })}
                />
              </div>
              <Toggle
                label="Centre the labels on the page"
                checked={settings.centerGrid}
                onChange={(v) => update({ centerGrid: v })}
              />
            </section>

            <button
              type="button"
              className="qrd-reset"
              onClick={() => setSettings({ ...DEFAULT_SETTINGS })}
            >
              Reset all settings
            </button>
          </div>

          {/* ---------------- preview ---------------- */}
          <div className="qrd-preview">
            <div className="qrd-summary">
              <strong>
                {(layout.cell.w / 10).toFixed(1)} ×{" "}
                {(layout.cell.h / 10).toFixed(1)} cm
              </strong>{" "}
              label · {perPageInfo}
              <br />
              <b>{labelCount}</b> label{labelCount !== 1 ? "s" : ""} on{" "}
              <b>{pages.length}</b> page{pages.length !== 1 ? "s" : ""}
            </div>

            {layout.warnings.map((w) => (
              <p key={w} className="qrd-warning">
                ⚠ {w}
              </p>
            ))}

            {pages.length ? (
              <>
                <div
                  className="qrd-page-wrap"
                  style={{ width: PREVIEW_W, height: previewH }}
                >
                  <div
                    style={{
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                      width: `${layout.pw}mm`,
                    }}
                  >
                    <LabelPage
                      labels={pages[previewPage] || []}
                      layout={layout}
                      s={settings}
                      isLast
                    />
                  </div>
                </div>
                <div className="qrd-pager">
                  <button
                    type="button"
                    disabled={previewPage === 0}
                    onClick={() => setPreviewPage((p) => p - 1)}
                  >
                    ‹
                  </button>
                  <span>
                    Page {previewPage + 1} of {pages.length}
                  </span>
                  <button
                    type="button"
                    disabled={previewPage >= pages.length - 1}
                    onClick={() => setPreviewPage((p) => p + 1)}
                  >
                    ›
                  </button>
                </div>
              </>
            ) : (
              <p className="qrd-note">Select at least one QR code to print.</p>
            )}

            <div className="qrd-buttons">
              <button
                type="button"
                className="primary"
                onClick={print}
                disabled={!pages.length}
              >
                🖨 Print{" "}
                {pages.length
                  ? `${pages.length} page${pages.length !== 1 ? "s" : ""}`
                  : ""}
              </button>
              <button type="button" onClick={openTestScan}>
                Test scan popup
              </button>
            </div>
            <p className="qrd-note small">
              In the print dialog choose{" "}
              <strong>Scale 100% / Actual size</strong> and turn off “Headers
              and footers”, otherwise the QR will not print at the size shown.
              Choose “Save as PDF” to keep a copy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockQRModal;
